// src/lib/useAuth.js
import { useEffect, useMemo, useState, createContext, useContext } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [mode, setMode] = useState(() => localStorage.getItem("uh_mode") || "guest");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unSubDoc = null;

    const off = onAuthStateChanged(auth, (u) => {
      setUser(u);

      // stop any prior /users/{uid} subscription
      if (unSubDoc) { unSubDoc(); unSubDoc = null; }

      // ---- Bridge auth state to the landing (same-origin only) ----
      try {
        if (u) {
          localStorage.setItem("yw:isAuthed", "1");
          localStorage.setItem("yw:userName", u.displayName || "");
        } else {
          localStorage.removeItem("yw:isAuthed");
          localStorage.removeItem("yw:userName");
        }
        // notify other tabs / the Next landing immediately
        window.dispatchEvent(new Event("yw-auth-change"));
        // also poke storage to trigger its event across tabs
        localStorage.setItem("yw:ping", String(Date.now()));
      } catch {
        // ignore storage issues in rare environments
      }
      // -------------------------------------------------------------

      if (!u) {
        setRoles([]);
        setLoading(false);
        return;
      }

      // live subscribe to user's roles
      const ref = doc(db, "users", u.uid);
      unSubDoc = onSnapshot(
        ref,
        (snap) => {
          const data = snap.exists() ? snap.data() : {};
          setRoles(Array.isArray(data.roles) ? data.roles : []);
          setLoading(false);
        },
        () => {
          setRoles([]);
          setLoading(false);
        }
      );
    });

    return () => {
      off();
      if (unSubDoc) unSubDoc();
    };
  }, []);

  const value = useMemo(
    () => ({
      user,
      roles,
      loading,
      mode,
      setMode: (m) => {
        localStorage.setItem("uh_mode", m);
        setMode(m);
      },
    }),
    [user, roles, loading, mode]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
